import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { swallowReleaseClick } from './useDragGesture';

/**
 * Pulling a dialog down to close it. On phones a dialog is a sheet rising from the bottom of the
 * screen, with a grabber on top when it can be closed (styles.css). The sheet follows a finger
 * pulling down on its title bar, or anywhere on it when its content is scrolled to the top (a
 * pull on content that is scrolled down scrolls it back up, as usual). Let go far enough down,
 * or with a flick, and it slides away and closes; otherwise it springs back. A mouse or a pen
 * pulls it by its title bar.
 */

/** Dialogs are sheets at this width and below (styles.css). */
const SHEET_LAYOUT = '(max-width: 640px)';
/** How far a press moves before it counts as a pull, or as something else, in CSS pixels. */
const SLOP = 6;
/** The pointer's speed is measured over this much of the latest movement, in ms. */
const VELOCITY_WINDOW_MS = 100;

/**
 * Whether a sheet let go `distance` px below where it rests, moving at `velocity` px/ms (positive
 * is down), closes: a flick down, or a quarter of the way (at least 60 px) unless pushed back up.
 */
export function sheetCloses(distance: number, velocity: number, height: number): boolean {
  if (distance <= 0) return false;
  if (velocity > 0.5 && distance > 16) return true;
  if (velocity < -0.3) return false;
  return distance > Math.max(60, height * 0.25);
}

interface Pull {
  readonly x0: number;
  readonly y0: number;
  readonly height: number;
  pulling: boolean;
  samples: { t: number; y: number }[];
}

const reduced = () => document.documentElement.classList.contains('reduce-motion');

/** Whether a press on `target` may pull the sheet: not in a text field nor in scrolled content. */
function canPullFrom(target: Element, sheet: Element): boolean {
  if (target.closest('input, textarea, select, [contenteditable="true"]')) return false;
  if (target.closest('.modal-head')) return true;
  for (let el: Element | null = target; el && el !== sheet; el = el.parentElement)
    if (el.scrollTop > 0) return false;
  return true;
}

export function useSheetSwipe(
  sheetRef: RefObject<HTMLElement | null>,
  onClose: (() => void) | undefined,
): void {
  const closeRef = useRef(onClose);
  useLayoutEffect(() => {
    closeRef.current = onClose;
  });
  const closable = !!onClose;

  useEffect(() => {
    const sheet = sheetRef.current;
    const backdrop = sheet?.parentElement;
    if (!sheet || !backdrop || !closable) return;
    let pull: Pull | null = null;
    // Sliding away or springing back: no new pull until it is done.
    let settling: ReturnType<typeof setTimeout> | undefined;
    let stopPointer: (() => void) | null = null;
    // The backdrop's shade (its color, as r, g, b, alpha), dimmed as the sheet goes down.
    let shade: number[] | null = null;

    const setShade = (k: number) => {
      if (!shade) return;
      const [r, g, b, a = 1] = shade;
      backdrop.style.backgroundColor = `rgba(${r}, ${g}, ${b}, ${(a * k).toFixed(3)})`;
    };
    // Back to the stylesheet's look, but for the opening animations: they stay off, so that
    // they do not play again.
    const reset = () => {
      for (const el of [sheet, backdrop]) {
        el.style.transform = '';
        el.style.transition = '';
        el.style.backgroundColor = '';
      }
      sheet.classList.remove('is-pulled');
      shade = null;
    };

    const start = (x: number, y: number, target: EventTarget | null): boolean => {
      if (!(target instanceof Element) || settling !== undefined) return false;
      if (!globalThis.matchMedia?.(SHEET_LAYOUT).matches || !canPullFrom(target, sheet))
        return false;
      const t = performance.now();
      pull = { x0: x, y0: y, height: sheet.offsetHeight, pulling: false, samples: [{ t, y }] };
      return true;
    };

    /** Follows the pointer; true while the sheet is being pulled (the move is the pull's). */
    const move = (x: number, y: number, mayTakeOver: boolean): boolean => {
      if (!pull) return false;
      const dx = x - pull.x0;
      const dy = y - pull.y0;
      if (!pull.pulling) {
        if (Math.hypot(dx, dy) < SLOP) return false;
        // Up, sideways, or already scrolling: not a pull.
        if (dy <= 0 || Math.abs(dx) > dy || !mayTakeOver) {
          pull = null;
          return false;
        }
        pull.pulling = true;
        sheet.classList.add('is-pulled');
        sheet.style.animation = 'none';
        sheet.style.transition = 'none';
        backdrop.style.animation = 'none';
        backdrop.style.transition = 'none';
        const color = /rgba?\(([^)]+)\)/.exec(getComputedStyle(backdrop).backgroundColor);
        shade = color?.[1]
          ? color[1]
              .split(/[\s,/]+/)
              .filter(Boolean)
              .map(Number)
          : null;
      }
      const t = performance.now();
      pull.samples.push({ t, y });
      while (pull.samples.length > 2 && t - pull.samples[0]!.t > VELOCITY_WINDOW_MS)
        pull.samples.shift();
      const offset = Math.max(0, dy);
      sheet.style.transform = `translateY(${offset}px)`;
      setShade(1 - offset / Math.max(1, pull.height));
      return true;
    };

    const end = (x: number, y: number, cancelled: boolean) => {
      const p = pull;
      pull = null;
      if (!p?.pulling) return;
      swallowReleaseClick(x, y);
      const first = p.samples[0]!;
      const last = p.samples[p.samples.length - 1]!;
      const velocity = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0;
      const distance = Math.max(0, y - p.y0);
      if (!cancelled && sheetCloses(distance, velocity, p.height)) {
        // Away at the speed it was going (a quick finish when let go slowly).
        const rest = p.height + 24 - distance;
        const ms = reduced()
          ? 0
          : Math.round(Math.min(260, Math.max(120, rest / Math.max(velocity, 1.4))));
        sheet.style.transition = `transform ${ms}ms cubic-bezier(0.25, 0.7, 0.4, 1)`;
        sheet.style.transform = `translateY(${p.height + 24}px)`;
        backdrop.style.transition = `background-color ${ms}ms linear`;
        setShade(0);
        settling = setTimeout(() => {
          settling = undefined;
          closeRef.current?.();
          // Closing unmounts the dialog; one that stays open after all comes back.
          settling = setTimeout(() => {
            settling = undefined;
            reset();
          }, 400);
        }, ms);
      } else {
        const ms = reduced() ? 0 : 220;
        sheet.style.transition = `transform ${ms}ms var(--ease)`;
        sheet.style.transform = 'translateY(0)';
        backdrop.style.transition = `background-color ${ms}ms linear`;
        setShade(1);
        settling = setTimeout(() => {
          settling = undefined;
          reset();
        }, ms + 20);
      }
    };

    // Fingers: anywhere on the sheet. The listener for moves is not passive, so that a pull can
    // keep the content from scrolling (or the page from bouncing) while the sheet follows.
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (e.touches.length !== 1 || !touch) {
        if (pull?.pulling) end(pull.x0, pull.y0, true);
        pull = null;
        return;
      }
      start(touch.clientX, touch.clientY, e.target);
    };
    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!pull || !touch) return;
      if (move(touch.clientX, touch.clientY, e.cancelable) && e.cancelable) e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      if (!pull || !touch) return;
      end(touch.clientX, touch.clientY, e.type === 'touchcancel');
    };

    // A mouse or a pen: by the title bar.
    const head = sheet.querySelector<HTMLElement>('.modal-head');
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'touch' || e.button !== 0 || !e.isPrimary) return;
      if (!start(e.clientX, e.clientY, e.target)) return;
      const id = e.pointerId;
      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId === id && move(ev.clientX, ev.clientY, true)) ev.preventDefault();
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== id) return;
        stop();
        end(ev.clientX, ev.clientY, ev.type === 'pointercancel');
      };
      const stop = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        stopPointer = null;
      };
      stopPointer?.();
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
      stopPointer = stop;
    };

    sheet.addEventListener('touchstart', onTouchStart, { passive: true });
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('touchend', onTouchEnd);
    sheet.addEventListener('touchcancel', onTouchEnd);
    head?.addEventListener('pointerdown', onPointerDown);
    return () => {
      sheet.removeEventListener('touchstart', onTouchStart);
      sheet.removeEventListener('touchmove', onTouchMove);
      sheet.removeEventListener('touchend', onTouchEnd);
      sheet.removeEventListener('touchcancel', onTouchEnd);
      head?.removeEventListener('pointerdown', onPointerDown);
      stopPointer?.();
      clearTimeout(settling);
    };
  }, [sheetRef, closable]);
}
